import React, { useMemo } from "react";
import { Container, Text, Sprite } from "@pixi/react";
import { TextStyle } from "pixi.js";
import * as PIXI from "pixi.js";
import { useTranslation } from "react-i18next";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import {
  FONT_CONFIG,
  USER_INTERFACE_BUTTON_CONFIG,
} from "../../../../textureConfigs";
import { PixiSettings } from "../../../../pixiSettings";
import { getPaylinesSlideImageConfig } from "./paytableTextConfig";

const PaylinesSlide = ({ viewportConfig, slideIndex }) => {
  const { t } = useTranslation();
  const { uiSymbolScale, getResponsiveConfig, device, orientation } =
    useDimensions();

  // Device-specific image configuration for Paylines slide
  const imageConfig = useMemo(
    () => getPaylinesSlideImageConfig(device, orientation),
    [device, orientation]
  );

  // Get button config for responsive spacing
  const buttonConfig = useMemo(
    () => getResponsiveConfig(USER_INTERFACE_BUTTON_CONFIG),
    [getResponsiveConfig]
  );

  // Calculate title font size (same as PopiPaytable title)
  const baseTitleFontSize = 46;
  const titleFontSize = useMemo(
    () =>
      Math.round(
        baseTitleFontSize *
          viewportConfig.titleFontSizeMultiplier *
          uiSymbolScale
      ),
    [viewportConfig.titleFontSizeMultiplier, uiSymbolScale]
  );

  // Title text style - yellow color
  const titleStyle = useMemo(
    () =>
      new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: titleFontSize,
        fontWeight: "bold",
        fill: 0xfeff00, // Yellow color
        align: "center",
      }),
    [titleFontSize]
  );

  // Determine which paylines image to use based on orientation and slide index
  // slideIndex 5 = first paylines slide (page 1), slideIndex 6 = second paylines slide (page 2)
  // Determine orientation: portrait images for mobile/tablet portrait, landscape for everything else
  const isPortrait =
    (device === PixiSettings.DEVICE.MOBILE ||
      device === PixiSettings.DEVICE.TABLET) &&
    orientation === PixiSettings.ORIENTATION.PORTRAIT;

  const orientationSuffix = isPortrait ? "portrait" : "landscape";

  // Determine page number: slideIndex 5 = page 1, slideIndex 6 = page 2
  const pageNumber = slideIndex === 5 ? "01" : "02";

  const assetName = `paylines_${pageNumber}_${orientationSuffix}`;

  // Load texture (similar to ReelSet.jsx)
  const payLinesTexture = useMemo(() => {
    return PIXI.Assets.get(assetName);
  }, [assetName]);

  // Calculate title position - just below the separator line
  // The separator line is at viewportY - titleFontSize * 2.0
  // Title should be positioned just below it, so near the top of viewport
  const titleY = titleFontSize * 0.3; // Position just below the separator line

  // Calculate spacing after title (responsive based on button config) - increased gap
  const titleSpacing = buttonConfig.height * 0.8;

  // Calculate available space for sprite (viewport height minus title and spacing)
  const availableHeight =
    viewportConfig.viewportHeight - titleY - titleFontSize - titleSpacing;

  // Calculate sprite dimensions maintaining aspect ratio using image config
  const spriteAspectRatio = payLinesTexture
    ? payLinesTexture.width / payLinesTexture.height
    : 1;

  // Calculate max dimensions from config
  const maxSpriteWidth = viewportConfig.maxWidth * imageConfig.imageWidthMultiplier;
  const maxSpriteHeight = availableHeight * imageConfig.imageHeightMultiplier;

  // Calculate sprite size to fit in available space
  let spriteWidth = maxSpriteWidth;
  let spriteHeight = spriteWidth / spriteAspectRatio;

  // If height exceeds available space, scale down
  if (spriteHeight > maxSpriteHeight) {
    spriteHeight = maxSpriteHeight;
    spriteWidth = spriteHeight * spriteAspectRatio;
  }

  // Center sprite vertically in the remaining viewport space with config offsets
  // Start from after title, then center in available height
  const spriteStartY = titleY + titleFontSize + titleSpacing;
  const offsetY = availableHeight * imageConfig.offsetY; // Convert multiplier to pixels
  const offsetX = viewportConfig.maxWidth * imageConfig.offsetX; // Convert multiplier to pixels
  const spriteY = spriteStartY + availableHeight / 2.15 + offsetY; // Center in available space with offset
  const spriteX = offsetX; // Apply horizontal offset from config

  // Guard: Don't render until texture is loaded (after all hooks and calculations)
  if (!payLinesTexture) {
    console.log(`[PaylinesSlide] Waiting for ${assetName} to load`);
    return null;
  }

  return (
    <Container name={`secondary_paylines_slide_${slideIndex}`}>
      {/* Subtitle */}
      <Text
        text={t("payline_title")}
        style={titleStyle}
        anchor={{ x: 0.5, y: 0 }}
        x={0}
        y={Math.round(titleY)}
        resolution={2}
      />

      {/* Pay Lines Sprite */}
      {payLinesTexture && (
        <Sprite
          texture={payLinesTexture}
          anchor={0.5}
          x={Math.round(spriteX)}
          y={Math.round(spriteY)}
          width={spriteWidth}
          height={spriteHeight}
        />
      )}
    </Container>
  );
};

export default PaylinesSlide;
